import { Component, computed, effect, input, model, OnDestroy, output, signal } from '@angular/core';
import { ControlsOf, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { Subscription } from 'rxjs';
import { debounceTime, finalize, switchMap, tap } from 'rxjs/operators';
import { Planning } from 'src/app/classes/planning.class';
import { Project } from 'src/app/classes/project.class';
import { Realisation, RealisationHourtype } from 'src/app/classes/realisation';
import { User } from 'src/app/classes/user.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { NotEditablePipe } from 'src/app/pages/check-hours/not-editable.pipe';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { Features } from 'src/app/services/auth/features';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { EntityId, EntityTypeCode } from 'src/app/services/entities/entity-type.class';
import { RealisationService } from 'src/app/services/realisation.service';
import { TimePickerService } from 'src/app/services/time-picker.service';
import { LocalStorage } from 'src/app/storage.class';
import { Utils } from 'src/app/utils.class';
import { environment } from 'src/environments/environment';
import type { FgRealisation, RealisationGroup, RealisationGroupingType, RealisationRow } from './hour-control.types';

type SelectionEvent = Event | { srcEvent?: Event };

@Component({
    selector: 'app-hour-control',
    standalone: false,
    templateUrl: './hour-control.component.html',
    styleUrl: './hour-control.component.scss',
})
export class HourControlComponent extends CodaltComponent implements OnDestroy {
    readonly realisations = input<Realisation[]>([]);
    readonly groupingType = input<RealisationGroupingType>('planning_hourtype_material');

    readonly selectedRealisations = model<Realisation[]>([]);
    readonly copyPreviousDayRequested = output<RealisationGroup>();
    readonly projectCommentRequested = output<Project>();
    readonly projectEditRequested = output<Realisation>();
    readonly projectsEditRequested = output<Realisation[]>();
    readonly projectOpenRequested = output<Project>();

    readonly allowEdit = input(true);
    readonly loadingData = input(false);
    readonly highlight = input<number | null>(null);
    readonly executors = input<User[]>([]);
    readonly showContextPerformer = input(false);

    readonly changedPauseUserId = signal<number | null>(null);

    readonly formMap = signal<Map<Realisation, FormGroup<ControlsOf<FgRealisation>>>>(new Map());

    readonly realisationGroups = computed(() => {
        const realisations = this.realisations();
        const formMap = this.formMap();

        // Als niet alle realisations zijn omgezet naar een formMap, early reaturn
        if (!realisations.every(realisation => formMap.has(realisation))) {
            return [];
        }

        return this.groupRealisations(realisations, this.groupingType()).map(group => this.withRows(group));
    });

    readonly userPauseMap = computed(() => {
        const formMap = this.formMap();
        const map = new Map<number, number>();

        this.realisations().forEach(realisation => {
            const form = formMap.get(realisation)!;

            if (form.controls.removed.value || !realisation.user_id) {
                return;
            }

            map.set(realisation.user_id, (map.get(realisation.user_id) ?? 0) + realisation.pause);
        });

        return map;
    });

    readonly userMinutesMap = computed(() => {
        const formMap = this.formMap();
        const map = new Map<number, number>();

        this.realisations().forEach(realisation => {
            const form = formMap.get(realisation)!;

            if (form.controls.removed.value || !realisation.user_id) {
                return;
            }

            map.set(
                realisation.user_id,
                (map.get(realisation.user_id) ?? 0) +
                    Utils.minuteDuration(form.controls.enddate.value, form.controls.begindate.value)
            );
        });

        return map;
    });

    readonly entityMinutesMap = computed(() => {
        const formMap = this.formMap();
        const map = new Map<string, number>();

        this.realisations().forEach(realisation => {
            const form = formMap.get(realisation)!;

            if (form.controls.removed.value || !realisation.entity_id) {
                return;
            }

            const key = this.getEntityMinutesKey(realisation);

            map.set(
                key,
                (map.get(key) ?? 0) + Utils.minuteDuration(form.controls.enddate.value, form.controls.begindate.value)
            );
        });

        return map;
    });

    private formSubscriptions = new Subscription();
    private confirmedRealisations = new WeakMap<Realisation, Realisation>();
    private daySubscriptions = new Subscription();
    private expandedGroups = new Map<string, boolean>();
    private groupedRealisations?: Realisation[];
    private longPressedRealisationId?: number;

    constructor(
        private timePickerService: TimePickerService,
        private realisationService: RealisationService,
        private confirmDialogService: ConfirmDialogService
    ) {
        super();

        effect(() => {
            this.rebuildForms(this.realisations());
        });
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();

        this.formSubscriptions.unsubscribe();
        this.daySubscriptions.unsubscribe();
    }

    private rebuildForms(realisations: Realisation[]): void {
        if (realisations !== this.groupedRealisations) {
            this.expandedGroups.clear();
            this.groupedRealisations = realisations;
        }

        this.formSubscriptions.unsubscribe();
        this.formSubscriptions = new Subscription();

        const formMap = new Map<Realisation, FormGroup<ControlsOf<FgRealisation>>>();

        realisations.forEach(realisation => {
            const formGroup = this.createRealisationForm(realisation);

            formMap.set(realisation, formGroup);
        });

        this.formMap.set(formMap);
    }

    private groupRealisations(realisations: Realisation[], groupingType: RealisationGroupingType): RealisationGroup[] {
        switch (groupingType) {
            case 'project_category':
                return this.groupByProjectAndCategory(realisations);

            case 'planning_hourtype':
                return this.groupByPlanningAndHourtype(realisations);

            case 'planning_hourtype_material':
                return this.groupByPlanningWithHourtypesAndMaterial(realisations);
        }
    }

    private groupByProjectAndCategory(realisations: Realisation[]): RealisationGroup[] {
        const groupedByProject = this.groupBy(realisations, realisation => {
            const bundleId =
                this.environment.checkHoursBundle === 'planning_id'
                    ? realisation.planning_id
                    : (Utils.realisationAfasProjectId(realisation) ?? realisation.afas_project_id);

            return String(bundleId ?? 'unknown');
        });

        return [...groupedByProject.entries()]
            .map(([projectId, projectRealisations]): RealisationGroup => {
                const groupedByPlanningId = this.environment.checkHoursBundle === 'planning_id';
                const planning = projectRealisations.find(realisation =>
                    groupedByPlanningId || projectId === 'unknown'
                        ? realisation.planning
                        : realisation.planning?.afas_project_id === projectId
                )?.planning;
                const userPlanning = projectRealisations.find(realisation =>
                    groupedByPlanningId
                        ? realisation.user_planning
                        : realisation.user_planning?.afas_project_id === projectId
                )?.user_planning;

                const project =
                    planning?.project ??
                    projectRealisations.find(r => r.project && (groupedByPlanningId || r.afas_project_id === projectId))
                        ?.project ??
                    userPlanning?.project;

                const sortedRealisations = this.sortRealisations(projectRealisations);
                const mainRealisation = this.getMainRealisation(sortedRealisations, planning);

                return {
                    key: `project-${projectId}`,
                    title: project?.name ?? (projectId !== 'unknown' ? `Project ${projectId}` : 'Geen project'),
                    type: 'project',
                    expanded: true,
                    project,
                    planning,
                    mainRealisation,
                    groups: this.getProjectCategoryGroups(sortedRealisations, project, planning, mainRealisation),
                };
            })
            .filter(group => (group.groups?.length ?? 0) > 0)
            .sort((a, b) => a.title.localeCompare(b.title));
    }

    private getProjectCategoryGroups(
        realisations: Realisation[],
        project?: Project,
        planning?: Planning,
        mainRealisation?: Realisation
    ): RealisationGroup[] {
        const groups: RealisationGroup[] = [
            {
                key: 'staff',
                title: 'Personeel',
                type: 'category',
                expanded: true,
                project,
                planning,
                mainRealisation,
                realisations: this.getUserRealisations(realisations),
            },
            {
                key: 'material',
                title: 'Materieel',
                type: 'material',
                expanded: false,
                project,
                planning,
                mainRealisation,
                realisations: this.getEntityRealisations(realisations),
            },
            {
                key: 'hired_material',
                title: 'Inhuur materieel',
                type: 'hired_material',
                expanded: false,
                project,
                planning,
                mainRealisation,
                realisations: this.getEntityHiringRealisations(realisations),
            },
            {
                key: 'hired_staff',
                title: 'Inhuur personeel',
                type: 'hired_staff',
                expanded: true,
                project,
                planning,
                mainRealisation,
                realisations: this.getHiredStaffRealisations(realisations),
            },
        ];

        return groups.filter(
            group => (group.realisations?.length ?? 0) > 0 || group.key === 'staff' || group.key === 'material'
        );
    }

    private groupByPlanningAndHourtype(realisations: Realisation[]): RealisationGroup[] {
        const groupedByPlanning = this.groupBy(realisations, realisation =>
            String(realisation.planning_id ?? 'unknown')
        );

        return [...groupedByPlanning.entries()]
            .map(([planningId, planningRealisations]): RealisationGroup => {
                const planning = planningRealisations.find(r => r.planning)?.planning;

                const project =
                    planning?.project ??
                    planningRealisations.find(r => r.project)?.project ??
                    planningRealisations.find(r => r.user_planning?.project)?.user_planning?.project;

                const sortedRealisations = this.sortRealisations(planningRealisations);
                const mainRealisation = this.getMainRealisation(sortedRealisations, planning);
                const groups = this.groupUserRealisationsByHourtype(sortedRealisations, project, planning);

                return {
                    key: `planning-${planningId}`,
                    title: this.getPlanningTitle(planningId, planningRealisations),
                    type: 'planning',
                    expanded: true,
                    project,
                    planning,
                    mainRealisation,
                    groups: this.sortByHourType(groups),
                };
            })
            .filter(group => (group.groups?.length ?? 0) > 0)
            .sort((a, b) => a.title.localeCompare(b.title));
    }

    private groupByPlanningWithHourtypesAndMaterial(realisations: Realisation[]): RealisationGroup[] {
        const groupedByPlanning = this.groupBy(realisations, realisation =>
            String(realisation.planning_id ?? 'unknown')
        );

        return [...groupedByPlanning.entries()]
            .map(([planningId, planningRealisations]): RealisationGroup => {
                const planning = planningRealisations.find(r => r.planning)?.planning;

                const project =
                    planning?.project ??
                    planningRealisations.find(r => r.project)?.project ??
                    planningRealisations.find(r => r.user_planning?.project)?.user_planning?.project;

                const sortedRealisations = this.sortRealisations(planningRealisations);
                const mainRealisation = this.getMainRealisation(sortedRealisations, planning);
                const groups = this.getPlanningChildGroups(sortedRealisations, project, planning);

                return {
                    key: `planning-${planningId}`,
                    title: this.getPlanningTitle(planningId, planningRealisations),
                    type: 'planning',
                    expanded: true,
                    project,
                    planning,
                    mainRealisation,
                    groups: this.sortByHourType(groups),
                };
            })
            .filter(group => (group.groups?.length ?? 0) > 0)
            .sort((a, b) => a.title.localeCompare(b.title));
    }

    private sortByHourType<T extends { key?: string; hourtype?: string }>(realisations: T[]) {
        return realisations.sort((a, b) => {
            const hourtypeA = a.key ? a.key.split('hourtype-')[1] : a.hourtype;
            const hourtypeB = b.key ? b.key.split('hourtype-')[1] : b.hourtype;

            const optionalOrder = 999;

            const sortOrderA =
                this.environment.realisationHourTypes.find(h => h.value === hourtypeA)?.sortOrder ?? optionalOrder;
            const sortOrderB =
                this.environment.realisationHourTypes.find(h => h.value === hourtypeB)?.sortOrder ?? optionalOrder;

            return sortOrderA - sortOrderB;
        });
    }

    /**
     * Determines if the default expansion state should be true based on the uniqueness of time ranges in the realisations.
     * @param realisations The list of realisations to check.
     * @returns True if there are multiple unique time ranges, false otherwise.
     */
    private defaultExpanded(realisations: Realisation[]): boolean {
        const timeString = (realisation: Realisation) =>
            `${Utils.getTimeOrNull(realisation.begindate)}-${Utils.getTimeOrNull(realisation.enddate)}`;
        return new Set(realisations.map(timeString)).size > 1;
    }

    private getPlanningChildGroups(
        realisations: Realisation[],
        project: Project,
        planning: Planning
    ): RealisationGroup[] {
        const userRealisations = this.getUserRealisations(realisations);

        const entityRealisations = this.getEntityRealisations(realisations);
        const entityHiringRealisations = this.getEntityHiringRealisations(realisations);
        const hiredRealisations = this.getHiredStaffRealisations(realisations);

        const minMaxTimesEntity = this.hourtypeRealisationDateRange(entityRealisations);
        const minMaxTimesEntityHiring = this.hourtypeRealisationDateRange(entityHiringRealisations);
        const minMaxTimesHired = this.hourtypeRealisationDateRange(hiredRealisations);

        const groups: RealisationGroup[] = [
            ...this.groupUserRealisationsByHourtype(userRealisations, project, planning),
            {
                key: 'material',
                title: 'Materieel',
                type: 'material',
                expanded: false,
                project,
                planning,
                mainRealisation: {
                    begindate: minMaxTimesEntity.smallestBegindate,
                    enddate: minMaxTimesEntity.largestEnddate,
                } as Realisation,
                realisations: this.sortByHourType(entityRealisations),
            },
            {
                key: 'hired_material',
                title: 'Inhuur materieel',
                type: 'hired_material',
                expanded: false,
                project,
                planning,
                mainRealisation: {
                    begindate: minMaxTimesEntityHiring.smallestBegindate,
                    enddate: minMaxTimesEntityHiring.largestEnddate,
                } as Realisation,
                realisations: this.sortByHourType(entityHiringRealisations),
            },
            {
                key: 'hired_staff',
                title: 'Inhuur personeel',
                type: 'hired_staff',
                expanded: true,
                project,
                planning,
                mainRealisation: {
                    begindate: minMaxTimesHired.smallestBegindate,
                    enddate: minMaxTimesHired.largestEnddate,
                } as Realisation,
                realisations: this.sortByHourType(hiredRealisations),
            },
        ];

        return groups.filter(group => {
            return (group.realisations?.length ?? 0) > 0 || (group.groups?.length ?? 0) > 0 || group.key === 'material';
        });
    }

    private hourtypeRealisationDateRange(hourtypeRealisations: Realisation[]) {
        return hourtypeRealisations.reduce(
            (range, realisation) => {
                const beginDate = realisation.begindate;
                const endDate = realisation.enddate;

                return {
                    smallestBegindate:
                        !range.smallestBegindate ||
                        (beginDate && new Date(beginDate).getTime() < new Date(range.smallestBegindate).getTime())
                            ? beginDate
                            : range.smallestBegindate,
                    largestEnddate:
                        !range.largestEnddate ||
                        (endDate && new Date(endDate).getTime() > new Date(range.largestEnddate).getTime())
                            ? endDate
                            : range.largestEnddate,
                };
            },
            {
                smallestBegindate: hourtypeRealisations[0]?.begindate,
                largestEnddate: hourtypeRealisations[0]?.enddate,
            }
        );
    }

    private groupUserRealisationsByHourtype(realisations: Realisation[], project: Project, planning: Planning) {
        const groupedByHourtype = this.groupBy(realisations, realisation => String(realisation.hourtype ?? 'unknown'));

        return [...groupedByHourtype.entries()]
            .map(([hourtype, hourtypeRealisations]) => {
                const minMaxTimes = this.hourtypeRealisationDateRange(hourtypeRealisations);

                return {
                    key: `hourtype-${hourtype}`,
                    title: this.getHourtypeTitle(hourtype),
                    type: 'hourtype',
                    expanded: this.defaultExpanded(hourtypeRealisations),
                    project,
                    planning,
                    mainRealisation: {
                        begindate: minMaxTimes.smallestBegindate,
                        enddate: minMaxTimes.largestEnddate,
                    } as Realisation,
                    realisations: this.sortRealisations(hourtypeRealisations),
                } as RealisationGroup;
            })
            .filter(group => (group.realisations?.length ?? 0) > 0)
            .sort((a, b) => a.title.localeCompare(b.title));
    }

    private getUserRealisations(realisations: Realisation[]): Realisation[] {
        return realisations.filter(realisation => !!realisation.user_id);
    }

    private getEntityRealisations(realisations: Realisation[]): Realisation[] {
        return realisations.filter(realisation => {
            return !!realisation.entity_id && !!realisation.entity?.use_once;
        });
    }

    private getHiredStaffRealisations(realisations: Realisation[]): Realisation[] {
        return this.getHiringEntities(realisations).filter(realisation => {
            return (
                realisation.planning_has?.entitytype_key === EntityTypeCode.InhuurPersoneel ||
                realisation.entity_id === EntityId.HiringEmployee
            );
        });
    }

    private getEntityHiringRealisations(realisations: Realisation[]): Realisation[] {
        return this.getHiringEntities(realisations).filter(realisation => {
            return (
                (realisation.planning_has &&
                    realisation.planning_has?.entitytype_key !== EntityTypeCode.InhuurPersoneel) ||
                realisation.entity_id === EntityId.HiringMaterials
            );
        });
    }

    private getHiringEntities(realisations: Realisation[]): Realisation[] {
        return realisations.filter(realisation => {
            return !!realisation.entity_id && !realisation.entity?.use_once && `${realisation.entity?.name}` !== 'GEEN';
        });
    }

    private groupBy<T>(items: T[], getKey: (item: T) => string): Map<string, T[]> {
        return items.reduce((groups, item) => {
            const key = getKey(item);

            if (!groups.has(key)) {
                groups.set(key, []);
            }

            groups.get(key)!.push(item);

            return groups;
        }, new Map<string, T[]>());
    }

    private sortRealisations(realisations: Realisation[]): Realisation[] {
        return [...realisations]
            .filter(realisation => {
                const removedOnLoad = !!realisation.removed;

                // Keep removed hours visible to the remover for undo, or when manager feedback still needs attention.
                return (
                    realisation.removed_by_id === LocalStorage.user.id ||
                    !removedOnLoad ||
                    realisation.comment_manager?.length > 0 ||
                    Utils.parentRealisations(realisation, this.realisations()).some(r => r.comment_manager?.length > 0)
                );
            })
            .filter(realisation => {
                return !realisation.planning_has?.entitytype?.is_team || !!realisation.planning_has_entity_id;
            })
            .sort((a, b) => {
                const beginDate = (realisation: Realisation) =>
                    Utils.getTimeOrNull(
                        this.groupingType() === 'planning_hourtype_material'
                            ? realisation.begindate
                            : (realisation.planning_has?.begindate ??
                                  realisation.user_planning?.begindate ??
                                  realisation.begindate)
                    );

                return beginDate(a) - beginDate(b);
            })
            .sort((a, b) => this.getRealisationSortString(a).localeCompare(this.getRealisationSortString(b)));
    }

    private getRealisationSortString(realisation: Realisation): string {
        if (realisation.user) {
            return [
                realisation.user.function,
                realisation.user.lastname,
                realisation.user.firstname,
                realisation.user.name,
            ]
                .filter(Boolean)
                .join('_');
        }

        if (realisation.entity) {
            return [realisation.entity.name, realisation.hiring_name].filter(Boolean).join('_');
        }

        return realisation.hiring_name ?? '';
    }

    private getMainRealisation(realisations: Realisation[], planning?: Planning): Realisation {
        return [...realisations]
            .sort((a, b) => {
                return Utils.minuteDuration(b.enddate, b.begindate) - Utils.minuteDuration(a.enddate, a.begindate);
            })
            .find(realisation => {
                if (!planning) {
                    return realisation.hourtype === RealisationHourtype.worktime;
                }

                if (
                    this.groupingType() === 'planning_hourtype' ||
                    this.groupingType() === 'planning_hourtype_material' ||
                    (this.groupingType() === 'project_category' && this.environment.checkHoursBundle === 'planning_id')
                ) {
                    return (
                        planning.performer === realisation.user_id &&
                        realisation.hourtype === RealisationHourtype.worktime
                    );
                }

                return (
                    ((!!planning?.planning_has_id &&
                        realisation.planning_has_entity_id === planning?.planning_has_id) ||
                        !planning?.planning_has_id) &&
                    realisation.hourtype === RealisationHourtype.worktime
                );
            });
    }

    private getPlanningTitle(planningId: string, realisations: Realisation[]): string {
        const planning = realisations.find(r => r.planning)?.planning;
        const userPlanning = realisations.find(r => r.user_planning)?.user_planning;

        if (planning?.project?.name) {
            return planning.project.name;
        }

        if (userPlanning?.project?.name) {
            return userPlanning.project.name;
        }

        if (planning?.worknumber) {
            return `Planning ${planning.worknumber}`;
        }

        if (planningId !== 'unknown') {
            return `Planning ${planningId}`;
        }

        return 'Geen planning';
    }

    private getHourtypeTitle(hourtype: string): string {
        const title = environment?.realisationHourTypes.find(h => h.value === hourtype)?.label;
        return title ?? hourtype ?? 'Onbekend';
    }

    private withRows(group: RealisationGroup, project = group.project, parentKey = ''): RealisationGroup {
        const expansionKey = `${parentKey}/${group.key}`;
        let names = [];

        if (this.groupingType() === 'planning_hourtype_material') {
            names = [
                ...new Map<number, string>(
                    (group.realisations ?? [])
                        .filter(realisation => realisation.user_id && realisation.user?.firstname)
                        .map(realisation => [realisation.user_id!, realisation.user!.firstname] as [number, string])
                ).values(),
            ];
        }

        return {
            ...group,
            expanded: this.expandedGroups.get(expansionKey) ?? group.expanded,
            expansionKey,
            collapsedNames: names,
            groups: group.groups?.map(childGroup =>
                this.withRows(childGroup, childGroup.project ?? project, expansionKey)
            ),
            rows: group.realisations ? this.createRows(group.realisations, project) : undefined,
        };
    }

    private createRows(realisations: Realisation[], project?: Project): RealisationRow[] {
        const allRealisations = this.realisations();
        const formMap = this.formMap();

        return realisations.map((realisation, index): RealisationRow => {
            const visualGroupKey = this.getVisualGroupKey(realisation);
            const previousVisualGroupKey = this.getVisualGroupKey(realisations[index - 1]);
            const nextVisualGroupKey = this.getVisualGroupKey(realisations[index + 1]);

            const isFirstGroup = visualGroupKey !== previousVisualGroupKey;
            const isLastGroup = visualGroupKey !== nextVisualGroupKey;
            const isMiddle = visualGroupKey === previousVisualGroupKey && visualGroupKey !== nextVisualGroupKey;

            const showApproveAll = isMiddle && !realisation.approved;
            // realisation.performer_id ("doorgezette uren") is a manual override and always wins,
            // matching buildRealisationQueryWithContext() on the backend. Below that, a designated
            // hour-checker (by id or by role) outranks the asphalt planning/project performer.
            const hasDesignatedChecker = !!(
                realisation.user?.hours_checked_by || realisation.user?.hours_checked_by_role
            );
            const performerId =
                realisation.performer_id ??
                (hasDesignatedChecker
                    ? realisation.user?.hours_checked_by
                    : (realisation.user_planning?.performer_id ??
                      realisation.planning_has?.performer_id ??
                      realisation.planning?.asphalt_performer ??
                      realisation.planning?.performer ??
                      project?.asphalt_performer_id ??
                      project?.performer_id));
            const performerName =
                this.executors().find(executor => executor.id === performerId)?.name ??
                // No fixed checker id (role-based only): show the role's label instead of nothing.
                (hasDesignatedChecker ? realisation.user?.hours_checked_role?.name : undefined);
            const driverRealisation = allRealisations
                .filter(candidate => {
                    return (
                        (!!candidate.parent_realisation_id &&
                            candidate.parent_realisation_id === realisation.parent_realisation_id) ||
                        (!!candidate.planning_has_entity_id &&
                            candidate.planning_has_entity_id === realisation.planning_has_entity_id)
                    );
                })
                .find(candidate => candidate.hourtype === realisation.hourtype && !!candidate.user_id);
            return {
                realisation,
                form: formMap.get(realisation)!,

                isFirstGroup,
                isLastGroup,

                showApproveAll,

                userRegisteredMinutes: realisation.user_id ? this.getUserRegisteredMinutes(realisation.user_id) : 0,
                entityRegisteredMinutes: realisation.entity_id ? this.getEntityRegisteredMinutes(realisation) : 0,

                pause: this.userPauseMap().get(realisation.user_id) ?? 0,
                pauseChanged: this.changedPauseUserId() === realisation.user_id,
                parentRealisationUserName: allRealisations.find(
                    candidate => candidate.id === realisation.parent_realisation_id
                )?.user?.name,
                performerId,
                performerName,
                showContextPerformer: this.showContextPerformer(),
                projectId: project?.afas_project_id,
                projectName: project?.name,
                driverRealisations: driverRealisation ? [driverRealisation] : [],
            };
        });
    }

    private getUserRegisteredMinutes(userId: number): number {
        return (this.userMinutesMap().get(userId) ?? 0) - (this.userPauseMap().get(userId) ?? 0);
    }

    private getEntityRegisteredMinutes(realisation: Realisation): number {
        return this.entityMinutesMap().get(this.getEntityMinutesKey(realisation)) ?? 0;
    }

    flattenGroupRealisations(group: RealisationGroup): Realisation[] {
        return [
            ...(group.realisations ?? []),
            ...(group.groups ?? []).flatMap(child => this.flattenGroupRealisations(child)),
        ];
    }

    toggleGroup(group: RealisationGroup): void {
        group.expanded = !group.expanded;

        if (group.expansionKey) {
            this.expandedGroups.set(group.expansionKey, group.expanded);
        }
    }

    showGroupTimes(mainGroup: RealisationGroup): boolean {
        return !!mainGroup.groups?.some(group =>
            group.realisations?.some(
                realisation => !!realisation.user_id && realisation.hourtype === RealisationHourtype.worktime
            )
        );
    }

    private getVisualGroupKey(realisation?: Realisation): string {
        if (!realisation) {
            return '';
        }

        return String(
            realisation.user_id ??
                `${realisation.entity_id}-${realisation.hiring_name ?? ''}-${realisation.planning_has_entity_id ?? ''}`
        );
    }

    getEntityMinutesKey(realisation: Realisation): string {
        return `${realisation.entity_id}-${
            realisation.hiring_name ? `${realisation.hiring_name}-${realisation.planning_has_entity_id}` : ''
        }`;
    }

    onEditGroupTimeClick(
        event: Event,
        group: RealisationGroup,
        field: 'begindate' | 'enddate',
        addToCurrentTime = 0
    ): void {
        this.stopEvent(event);
        this.editGroupTimes(event, group, field, addToCurrentTime);
    }

    private stopEvent(event?: SelectionEvent): void {
        const sourceEvent = event instanceof Event ? event : event?.srcEvent;

        sourceEvent?.preventDefault();
        sourceEvent?.stopPropagation();
    }

    approve(realisation: Realisation): void {
        if (!AuthorisationService.canApproveHours() || this.formMap().get(realisation)?.disabled) {
            return;
        }

        const commentHandledAt =
            realisation.approved && realisation.comment_user_approved && !realisation.comment_user_approved_handled
                ? new Date()
                : realisation.comment_user_approved_handled;
        const approved =
            commentHandledAt !== realisation.comment_user_approved_handled
                ? realisation.approved
                : (realisation.approved ?? new Date());
        const relatedEntityRealisations = this.realisations().filter(entityRealisation => {
            return (
                !!entityRealisation.entity_id &&
                (entityRealisation.planning_has_entity_id === (realisation.planning_has_entity_id ?? 'donotmapnull') ||
                    entityRealisation.parent_realisation_id === realisation.id)
            );
        });

        [realisation, ...relatedEntityRealisations].forEach(realisationToApprove => {
            const form = this.formMap().get(realisationToApprove);
            if (!form || form.disabled) {
                return;
            }

            const commentUserApprovedHandled =
                realisationToApprove === realisation
                    ? commentHandledAt
                    : realisationToApprove.comment_user_approved_handled;

            form.patchValue(
                {
                    approved,
                    comment_user_approved_handled: commentUserApprovedHandled,
                    skip_performer: false,
                },
                { emitEvent: false }
            );
            form.disable({ emitEvent: false });

            this.daySubscriptions.add(
                this.realisationService
                    .approveRealisation({
                        ...realisationToApprove,
                        approved,
                        comment_user_approved_handled: commentUserApprovedHandled,
                    })
                    .pipe(finalize(() => form.enable({ emitEvent: false })))
                    .subscribe({
                        next: response => this.setRealisations(response.data),
                        error: error => {
                            if (error.message === 'Conflict' && Array.isArray(error.data)) {
                                this.setRealisations(error.data);
                            } else {
                                this.revertRealisation(realisationToApprove);
                                this.confirmReload(
                                    'Goedkeuren mislukt',
                                    'Wil je de pagina verversen om de actuele gegevens op te halen?'
                                );
                            }
                        },
                    })
            );
        });
    }

    approveAllInGroup(group: RealisationGroup, realisation: Realisation): void {
        this.flattenGroupRealisations(group)
            .filter(groupRealisation => {
                return groupRealisation.user_id === realisation.user_id && !groupRealisation.approved;
            })
            .forEach(groupRealisation => this.approve(groupRealisation));
    }

    restore(realisation: Realisation): void {
        const form = this.formMap().get(realisation);
        if (form.disabled) {
            return;
        }

        form.controls.removed.setValue(null);
        form.controls.skip_performer.setValue(false);
        form.markAsDirty();

        if (realisation.user_id) {
            Utils.parentRealisations(realisation, this.realisations()).forEach(parentRealisation => {
                const parentForm = this.formMap().get(parentRealisation);

                parentForm.controls.removed.setValue(null);
                parentForm.controls.skip_performer.setValue(false);
                parentForm.markAsDirty();
            });
        }
    }

    remove(realisation: Realisation): void {
        const form = this.formMap().get(realisation);
        if (form.disabled) {
            return;
        }

        form.controls.removed.setValue(new Date());
        form.markAsDirty();

        if (realisation.user_id) {
            Utils.parentRealisations(realisation, this.realisations()).forEach(parentRealisation => {
                const parentForm = this.formMap().get(parentRealisation);

                parentForm.controls.removed.setValue(new Date());
                parentForm.markAsDirty();
            });
        }
    }

    editTime(realisation: Realisation, field: 'begindate' | 'enddate', addToCurrentTime = 0): void {
        const fgRealisation = this.formMap().get(realisation);

        const firstRealisationForAUser =
            realisation.user_id &&
            this.realisations()
                .filter(r => r.user_id === realisation.user_id && !this.formMap().get(r)?.controls.removed.value)
                .sort((a, b) => Utils.getTimeOrNull(a.begindate) - Utils.getTimeOrNull(b.begindate))[0]?.id ===
                realisation.id;

        this.daySubscriptions.add(
            this.timePickerService
                .editRealisationTime(realisation, field, fgRealisation, addToCurrentTime, firstRealisationForAUser)
                .subscribe(newDate => {
                    if (!newDate) {
                        return;
                    }

                    let newEndDate: Date = null;

                    if (field === 'begindate' && newDate.getTime() > new Date(realisation.enddate).getTime()) {
                        newEndDate = new Date(newDate);

                        do {
                            newEndDate.setMinutes(newEndDate.getMinutes() + 1);
                        } while (newEndDate.getMinutes() % 3 !== 0);
                    }

                    if (!!realisation.user_id) {
                        if (field === 'enddate') {
                            const diff =
                                Utils.getTimeOrNull(newDate) - Utils.getTimeOrNull(fgRealisation.get(field).value);

                            this.transferAllFutureTimes(realisation, diff);
                        }

                        this.updateRelatedRealisations(realisation, field, newDate, newEndDate);
                    }

                    fgRealisation.get(field).setValue(newDate);

                    if (newEndDate) {
                        const diff =
                            Utils.getTimeOrNull(newEndDate) - Utils.getTimeOrNull(fgRealisation.get('enddate').value);

                        this.transferAllFutureTimes(realisation, diff);
                        fgRealisation.get('enddate').setValue(newEndDate);
                    }

                    fgRealisation.markAsDirty();
                })
        );
    }

    editGroupTimes(event: Event, group: RealisationGroup, field: 'begindate' | 'enddate', addToCurrentTime = 0): void {
        event.stopPropagation();

        const mainRealisation = group.mainRealisation;

        if (!mainRealisation) {
            return;
        }

        const realisations = this.flattenGroupRealisations(group);

        const equalRealisations = realisations
            .filter(r => {
                return (
                    new Date(r.begindate).getTime() === new Date(mainRealisation.begindate).getTime() &&
                    new Date(r.enddate).getTime() === new Date(mainRealisation.enddate).getTime()
                );
            })
            .filter(r => !new NotEditablePipe().transform(r));

        if (!equalRealisations.length) {
            return;
        }

        const formGroup = this.formMap().get(equalRealisations[0]);

        this.daySubscriptions.add(
            this.timePickerService
                .editRealisationTime(equalRealisations[0], field, formGroup, addToCurrentTime)
                .subscribe(newDate => {
                    if (!newDate) {
                        return;
                    }

                    const oldEndTime = Utils.getTimeOrNull(mainRealisation.enddate);
                    const refreshGroup = (currentGroup: RealisationGroup) => {
                        const times = this.hourtypeRealisationDateRange(currentGroup.realisations ?? []);
                        currentGroup.mainRealisation = {
                            ...(currentGroup.mainRealisation ?? ({} as Realisation)),
                            begindate: times.smallestBegindate,
                            enddate: times.largestEnddate,
                        } as Realisation;
                    };

                    equalRealisations.forEach(realisation => {
                        const fg = this.formMap().get(realisation);

                        if (fg) {
                            this.updateRelatedRealisations(realisation, field, newDate, null);
                            fg.get(field).setValue(new Date(newDate));
                            fg.markAsDirty();
                        }
                    });

                    if (field === 'enddate') {
                        const affectedGroups = new Set<RealisationGroup>([group]);

                        this.realisations()
                            .filter(r => r.user_id && r.planning_id === equalRealisations[0].planning_id)
                            .filter(r => Utils.getTimeOrNull(r.begindate) === oldEndTime)
                            .filter(r => newDate.getTime() < new Date(r.enddate).getTime())
                            .forEach(realisation => {
                                const fg = this.formMap().get(realisation);

                                if (fg) {
                                    this.updateRelatedRealisations(realisation, 'begindate', newDate, null);
                                    fg.get('begindate').setValue(new Date(newDate));
                                    fg.markAsDirty();
                                }

                                const nextGroup = this.realisationGroups()
                                    .flatMap(parentGroup => parentGroup.groups ?? [])
                                    .find(childGroup => childGroup.realisations?.some(r => r.id === realisation.id));

                                if (nextGroup) {
                                    affectedGroups.add(nextGroup);
                                }
                            });

                        affectedGroups.forEach(refreshGroup);
                    } else {
                        refreshGroup(group);
                    }
                })
        );
    }

    updateRelatedRealisations(
        realisation: Realisation,
        field: 'begindate' | 'enddate',
        newDate: Date,
        newEndDate: Date
    ): void {
        const relatedEntityRealisations = this.realisations()
            .filter(entityRealisation => {
                return (
                    entityRealisation.planning_has_entity_id ===
                        (realisation.planning_has_entity_id ?? 'donotmapnull') ||
                    entityRealisation.parent_realisation_id === realisation.id
                );
            })
            .filter(entityRealisation => entityRealisation.hourtype === realisation.hourtype);

        relatedEntityRealisations.forEach(entityRealisation => {
            const fgEntityRealisation = this.formMap().get(entityRealisation);

            if (!fgEntityRealisation) {
                return;
            }

            const origBeginMatches =
                Utils.getTimeOrNull(realisation.begindate) === Utils.getTimeOrNull(entityRealisation.begindate);

            const origEndMatches =
                Utils.getTimeOrNull(realisation.enddate) === Utils.getTimeOrNull(entityRealisation.enddate);

            if (origBeginMatches && origEndMatches) {
                fgEntityRealisation.markAsDirty();
                fgEntityRealisation.get(field).setValue(new Date(newDate));

                if (newEndDate) {
                    fgEntityRealisation.get('enddate').setValue(new Date(newEndDate));
                }
            }
        });
    }

    transferAllFutureTimes(fromRealisation: Realisation, timeInSeconds: number): void {
        const userRealisations = this.realisations()
            .filter(r => r.user_id === fromRealisation.user_id)
            .sort((a, b) => Utils.realisationSort(a) - Utils.realisationSort(b));

        const fromIndex = userRealisations.indexOf(fromRealisation);

        const to = userRealisations.slice(fromIndex).find((r, index, realisations) => {
            return (
                index < realisations.length - 1 &&
                new Date(r.enddate).getTime() !== new Date(realisations[index + 1].begindate).getTime()
            );
        });

        const toIndex = to ? userRealisations.indexOf(to) : userRealisations.length - 1;

        userRealisations.slice(fromIndex, toIndex + 1).forEach((realisation, index) => {
            ['begindate', 'enddate'].forEach((fieldName: 'begindate' | 'enddate') => {
                if (index !== 0 || fieldName === 'enddate') {
                    const timeToSet = new Date(this.formMap().get(realisation).get(fieldName).value);

                    timeToSet.setTime(timeToSet.getTime() + timeInSeconds);

                    this.updateRelatedRealisations(realisation, fieldName, timeToSet, null);

                    this.formMap().get(realisation).get(fieldName).setValue(timeToSet);
                    this.formMap().get(realisation).markAsDirty();
                }
            });
        });
    }

    private createRealisationForm(realisation: Realisation): FormGroup<ControlsOf<FgRealisation>> {
        if (!this.confirmedRealisations.has(realisation)) {
            this.confirmedRealisations.set(realisation, { ...realisation });
        }

        const fg = new FormGroup<ControlsOf<FgRealisation>>({
            id: new FormControl(realisation.id),
            approved: new FormControl(realisation.approved),
            comment_user_approved_handled: new FormControl(realisation.comment_user_approved_handled),
            skip_performer: new FormControl(realisation.skip_performer),
            begindate: new FormControl(realisation.begindate),
            enddate: new FormControl(realisation.enddate),
            planning_id: new FormControl(realisation.planning_id),
            updated_at: new FormControl(realisation.updated_at),
            comment_user: new FormControl(realisation.comment_user),
            comment_manager: new FormControl(realisation.comment_manager),
            removed: new FormControl(realisation.removed),
        });

        this.formSubscriptions.add(
            fg.valueChanges
                .pipe(
                    tap(fgChanged => {
                        realisation.begindate = new Date(fgChanged.begindate);
                        realisation.enddate = fgChanged.enddate;
                        this.formMap.update(formMap => new Map(formMap));
                    }),
                    debounceTime(100)
                )
                .subscribe(fgChanged => {
                    if (!fg.dirty || fg.disabled) {
                        return;
                    }

                    fg.disable({ emitEvent: false });
                    const request$ =
                        fgChanged.skip_performer && fgChanged.approved
                            ? this.realisationService
                                  .approveRealisation({ ...(fgChanged as Realisation), approved: undefined })
                                  .pipe(
                                      tap(response =>
                                          this.setRealisations(response.data.filter(r => r.id === fgChanged.id))
                                      ),
                                      switchMap(response =>
                                          this.realisationService.saveRealisation({
                                              ...(fgChanged as Realisation),
                                              updated_at: response.data.find(r => r.id === fgChanged.id)!.updated_at,
                                          })
                                      )
                                  )
                            : this.realisationService.saveRealisation(fgChanged as Realisation);

                    this.daySubscriptions.add(
                        request$.pipe(finalize(() => fg.enable({ emitEvent: false }))).subscribe({
                            next: response => this.setRealisations([response.data.realisation]),
                            error: error => {
                                if (error.message === 'Conflict' && Array.isArray(error.data)) {
                                    this.setRealisations(error.data);
                                } else {
                                    this.revertRealisation(realisation);
                                    if (error.status !== 403 && error.status !== 401) {
                                        this.confirmReload(
                                            'Opslaan mislukt',
                                            (error.message || 'De wijziging kon niet worden opgeslagen.') +
                                                ' Wil je de pagina verversen om de actuele gegevens op te halen?'
                                        );
                                    }
                                }
                            },
                        })
                    );
                })
        );

        return fg;
    }

    private revertRealisation(realisation: Realisation): void {
        this.setRealisations([this.confirmedRealisations.get(realisation)!]);
    }

    private confirmReload(title: string, text: string): void {
        this.confirmDialogService
            .confirm({ title, text, confirm: 'Verversen', cancel: 'Sluiten' })
            .then(() => window.location.reload())
            .catch(() => {});
    }

    private setRealisations(serverRealisations: Realisation[]): void {
        serverRealisations.forEach(serverRealisation => {
            const localRealisation = this.realisations().find(r => r.id === serverRealisation.id);
            const localForm = this.formMap().get(localRealisation);

            if (!localRealisation || !localForm) {
                return;
            }

            Object.assign(localRealisation, serverRealisation);
            this.confirmedRealisations.set(localRealisation, { ...serverRealisation });
            localForm.patchValue(
                {
                    approved: serverRealisation.approved,
                    comment_user_approved_handled: serverRealisation.comment_user_approved_handled,
                    skip_performer: serverRealisation.skip_performer,
                    begindate: serverRealisation.begindate,
                    enddate: serverRealisation.enddate,
                    planning_id: serverRealisation.planning_id,
                    updated_at: serverRealisation.updated_at,
                    comment_user: serverRealisation.comment_user,
                    comment_manager: serverRealisation.comment_manager,
                    removed: serverRealisation.removed,
                },
                { emitEvent: false }
            );
            localForm.markAsPristine();
        });

        this.formMap.update(formMap => new Map(formMap));
    }

    updatePause(realisations: Realisation[], userId: number): void {
        this.setRealisations(realisations);
        this.changedPauseUserId.set(userId);
        setTimeout(() => this.changedPauseUserId.set(null), 1500);
    }

    selectRealisation(realisation: Realisation): void {
        if (Utils.isLeaveType(realisation)) {
            return;
        }

        const currentSelection = this.selectedRealisations();
        const index = currentSelection.findIndex(r => r.id === realisation.id);

        const relatedRealisations = [
            realisation,
            ...this.realisations().filter(r => r.parent_realisation_id === realisation.id),
            ...this.realisations().filter(r => r.id === realisation.parent_realisation_id),
            ...this.realisations().filter(
                r => r.planning_has_entity_id === (realisation.planning_has_entity_id ?? 'donotmapnull')
            ),
            ...this.realisations().filter(r => r.user_planning_id === (realisation.user_planning_id ?? 'donotmapnull')),
        ]
            .flat()
            .filter((r, realisationIndex, arr) => arr.findIndex(rr => rr.id === r.id) === realisationIndex);

        if (index > -1) {
            this.selectedRealisations.set(currentSelection.filter(r => !relatedRealisations.includes(r)));
        } else {
            this.selectedRealisations.set([...currentSelection, ...relatedRealisations]);
        }
    }

    longPressSelectionMode(realisation: Realisation, event: SelectionEvent): void {
        if (!AuthorisationService.hasFeature(Features.RealisationScheduleMovePerformerSave)) {
            return;
        }

        this.stopEvent(event);

        this.selectRealisation(realisation);

        this.longPressedRealisationId = realisation.id;
    }

    longPressSelectionModeEnd(realisation: Realisation, event?: SelectionEvent): void {
        this.stopEvent(event);

        if (this.longPressedRealisationId !== realisation.id) {
            return;
        }

        setTimeout(() => {
            if (this.longPressedRealisationId === realisation.id) {
                this.longPressedRealisationId = undefined;
            }
        });
    }

    toggleSelection(realisation: Realisation, event?: SelectionEvent): void {
        if (!AuthorisationService.hasFeature(Features.RealisationScheduleMovePerformerSave)) {
            return;
        }

        if (this.selectedRealisations().length === 0) {
            return;
        }

        this.stopEvent(event);

        if (this.longPressedRealisationId === realisation.id) {
            this.longPressedRealisationId = undefined;
            return;
        }

        this.selectRealisation(realisation);
    }

    clearSelection(): void {
        this.selectedRealisations.set([]);
        this.longPressedRealisationId = undefined;
    }
}
